import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User, Sparkles } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const AIResearchAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm Harsha's AI Research Assistant. I can answer questions about his research projects, technical skills, publications, and experience. What would you like to know?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Enhanced knowledge base
  const knowledgeBase = {
    greetings: {
      keywords: ['hi', 'hello', 'hey', 'greetings'],
      response: "Hello! I'm here to help you learn about Harsha's impressive research work. You can ask me about his projects (Sparse-ViT, SEWP-Net, Crowd Monitoring), skills, publications, or how to contact him."
    },
    'sparse-vit': {
      keywords: ['sparse', 'vit', 'vision transformer', 'efficiency', 'pruning'],
      response: "Sparse-ViT is Harsha's innovative Vision Transformer achieving 50% reduction in parameters (5.53M→2.86M) and FLOPs while IMPROVING accuracy by 1.6%! It uses adaptive sparse attention with Gumbel-Softmax for end-to-end differentiable training. This 'train sparse from scratch' approach is perfect for edge devices and mobile AI."
    },
    'sewp-net': {
      keywords: ['sewp', 'crowd', 'safety', 'neuro-symbolic', 'prediction'],
      response: "SEWP-Net is a breakthrough hybrid neuro-symbolic framework that predicts crowd-crush events 0.5 seconds ahead! It achieves 95% accuracy in hotspot detection by combining CNN-LSTM with physics-based heuristics. The system features real-time risk scoring (0-100 scale) and automated alerts for safety-critical scenarios."
    },
    'crowd monitoring': {
      keywords: ['crowd density', 'monitoring', 'yolo', 'detection', 'published'],
      response: "The Real-Time Crowd Density Monitoring system uses YOLOv8 for person detection + optical flow analysis. It provides a normalized congestion index (0-100) with automated alerts. This work was published in ISJEM 2025 (DOI: 10.55041/ISJEM05317) and runs on GPU-enabled edge hardware in real-time!"
    },
    research: {
      keywords: ['research', 'projects', 'work', 'what does he do'],
      response: "Harsha has 4 cutting-edge research projects: 1) Sparse-ViT (ongoing FYP) - efficient transformers, 2) Perception-Control Systems (manuscript) - autonomous navigation, 3) SEWP-Net (manuscript) - crowd safety AI, 4) Crowd Monitoring (PUBLISHED). His work spans efficient AI, computer vision, and safety-critical systems!"
    },
    skills: {
      keywords: ['skill', 'technology', 'programming', 'know', 'languages'],
      response: "Harsha's core skills include: PyTorch, Vision Transformers, CNN-LSTM architectures, Computer Vision, Real-time Systems, Python, C++, OpenCV, and Machine Learning. He has 89% average proficiency across 6 technical categories with expertise in both theory and practical implementation!"
    },
    publication: {
      keywords: ['publication', 'paper', 'published', 'journal', 'doi'],
      response: "Harsha has 1 published peer-reviewed journal article in ISJEM 2025 on 'Real-Time Crowd Density Monitoring' (DOI: 10.55041/ISJEM05317) and 2 manuscripts in preparation for IEEE conferences on Sparse-ViT and Perception-Control Systems."
    },
    education: {
      keywords: ['education', 'university', 'degree', 'cgpa', 'academic'],
      response: "Harsha is pursuing B.Tech in Computer Science at Jain University with an impressive 8.29/10 CGPA. He has strong coursework in ML, AI, Deep Learning, Computer Vision, and Mathematical Foundations. His academic excellence is backed by research discipline and self-directed learning."
    },
    experience: {
      keywords: ['experience', 'internship', 'work', 'job', 'oasis'],
      response: "Harsha was a Data Science Intern at Oasis Infobyte where he was recognized as 'Star Performer' for exceptional dedication and top-tier results. He demonstrated excellent analytical skills and produced outstanding project achievements."
    },
    contact: {
      keywords: ['contact', 'email', 'phone', 'reach', 'linkedin', 'github'],
      response: "You can reach Harsha at:\n📧 Email: harshabathala12@gmail.com\n📱 Phone: (+91) 6362017792\n💼 LinkedIn: linkedin.com/in/bathalaharsha\n💻 GitHub: github.com/harshabathala12\n\nFeel free to connect for research collaborations or opportunities!"
    },
    strengths: {
      keywords: ['strength', 'good at', 'expertise', 'best', 'advantage'],
      response: "Harsha's key strengths: 1) Deep technical expertise in efficient AI and computer vision, 2) Published research at undergraduate level, 3) Ability to bridge theory and practical deployment, 4) Strong in both neural and symbolic approaches, 5) Proven track record of innovative solutions (50% efficiency gains!)"
    },
    achievements: {
      keywords: ['achievement', 'award', 'recognition', 'accomplishment'],
      response: "Key achievements: 🏆 Published researcher (ISJEM 2025), ⭐ Star Performer at Oasis Infobyte, 🎓 Outstanding Dedication Award at National Youth Festival, 📚 5 professional certifications (IBM ML, React, IoT, Cybersecurity, Python), 💡 50% efficiency improvement in Vision Transformers!"
    }
  };

  const getAIResponse = (userInput) => {
    const input = userInput.toLowerCase();
    
    // Check each knowledge category
    for (const [category, data] of Object.entries(knowledgeBase)) {
      if (data.keywords.some(keyword => input.includes(keyword))) {
        return data.response;
      }
    }
    
    // Enhanced default responses based on input characteristics
    if (input.includes('?')) {
      return "That's a great question! I can provide detailed information about:\n\n🔬 Research Projects (Sparse-ViT, SEWP-Net, Crowd Monitoring)\n💻 Technical Skills & Expertise\n📄 Publications & Papers\n🎓 Education & Achievements\n📞 Contact Information\n\nWhat specific aspect interests you most?";
    }
    
    if (input.includes('help')) {
      return "I'm here to help! Try asking me:\n• 'Tell me about Sparse-ViT'\n• 'What are his key strengths?'\n• 'Show me his publications'\n• 'What skills does he have?'\n• 'How can I contact him?'\n\nI have comprehensive knowledge about Harsha's work!";
    }
    
    // Intelligent fallback
    return `I understand you're asking about: "${userInput}"\n\nWhile I don't have specific information on that, I can tell you about:\n\n🎯 Research: Sparse-ViT (50% efficiency gain!), SEWP-Net (95% accuracy), Crowd Monitoring (published)\n🛠️ Skills: PyTorch, Vision Transformers, CNN-LSTM, Computer Vision\n📚 Publications: 1 published paper, 2 manuscripts in prep\n📧 Contact: harshabathala12@gmail.com\n\nWhat would you like to explore?`;
  };

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    setTimeout(() => {
      const aiResponse = getAIResponse(input);
      const assistantMessage = { role: 'assistant', content: aiResponse };
      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 800);
  };

  const suggestedQuestions = [
    "What is Sparse-ViT?",
    "Tell me about SEWP-Net",
    "Show his publications",
    "What are his key strengths?",
    "How to contact him?"
  ];

  return (
    <>
      {!isOpen && (
        <motion.button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 left-6 z-50 p-4 bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-full shadow-2xl hover:shadow-blue-500/50"
          whileHover={{ scale: 1.1, rotate: 5 }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 1.5 }}
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
        </motion.button>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            className="fixed bottom-6 left-6 z-50 w-96 max-w-[calc(100vw-3rem)]"
          >
            <Card className="border-2 border-blue-200 shadow-2xl">
              <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-4 rounded-t-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Bot className="w-6 h-6" />
                    <Sparkles className="w-3 h-3 absolute -top-1 -right-1 text-yellow-300" />
                  </div>
                  <div>
                    <h3 className="font-bold">AI Research Assistant</h3>
                    <p className="text-xs text-blue-100">Powered by intelligent knowledge base</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="hover:bg-blue-800 p-2 rounded transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <CardContent className="p-4 h-96 overflow-y-auto bg-slate-50">
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex gap-2 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {message.role === 'assistant' && (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-5 h-5 text-blue-900" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] p-3 rounded-lg whitespace-pre-line ${
                          message.role === 'user'
                            ? 'bg-blue-900 text-white rounded-br-none'
                            : 'bg-white border border-gray-200 rounded-bl-none shadow-sm'
                        }`}
                      >
                        <p className="text-sm">{message.content}</p>
                      </div>
                      {message.role === 'user' && (
                        <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </motion.div>
                  ))}

                  {isTyping && (
                    <div className="flex gap-2 items-center">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-blue-900" />
                      </div>
                      <div className="bg-white border border-gray-200 p-3 rounded-lg">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></span>
                          <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                          <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {messages.length === 1 && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-600 mb-2 font-semibold">💡 Try asking:</p>
                    <div className="space-y-2">
                      {suggestedQuestions.map((question, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setInput(question);
                            setTimeout(handleSend, 100);
                          }}
                          className="w-full text-left text-xs p-2 bg-white hover:bg-blue-50 border border-gray-200 rounded transition-all hover:border-blue-300 hover:shadow-sm"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>

              <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                    placeholder="Ask anything about Harsha's work..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <button
                    onClick={handleSend}
                    disabled={!input.trim()}
                    className="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIResearchAssistant;
